/**
 * BrandSetupWizard — non-dismissible 4-step overlay that forces a country_operator
 * to complete their whitelabel essentials on first login. Steps:
 *   1. Company name + Subdomain
 *   2. Logo + Favicon
 *   3. Primary color
 *   4. Demo booking URL (optional) → Save
 *
 * Renders as a fixed overlay covering the dashboard. Once saved with all
 * required fields, the wizard sets `setup_completed:true` on the whitelabel
 * record so it never shows again. WhitelabelSetupBanner handles the lighter
 * post-completion nudge for any field that gets cleared later.
 */
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Loader2, Upload, Image as ImageIcon, Sparkles, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const ELIGIBLE_ROLES = ['country_operator', 'super_admin', 'client_admin'];

const isEligible = (user) => {
  if (!user || !ELIGIBLE_ROLES.includes(user.role)) return false;
  // client_admin (Enterprise customer of a Country Operator) only when their
  // plan is enterprise — same gate the backend uses in _self_service_eligible.
  if (user.role === 'client_admin' && (user.plan || '').toLowerCase() !== 'enterprise') return false;
  return true;
};

const COLORS = ['#10b981', '#0d9488', '#0ea5e9', '#6366f1', '#ec4899', '#f59e0b', '#ef4444', '#111827'];

export const BrandSetupWizard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    company_name: '',
    subdomain_slug: '',
    custom_domain: '',
    logo_url: '',
    favicon_url: '',
    primary_color: '#10b981',
    booking_url: '',
  });
  const logoRef = useRef(null);
  const faviconRef = useRef(null);

  // Decide whether to show the wizard. We open it when:
  //   - the user is an eligible role
  //   - AND the whitelabel record has setup_completed != true
  //   - AND the record is missing at least one essential
  useEffect(() => {
    if (!isEligible(user)) { setLoading(false); return; }
    let cancelled = false;
    axios.get(`${API}/api/whitelabel/me`).then((r) => {
      if (cancelled) return;
      const cfg = r.data?.config || {};
      const hasDomain = !!(cfg.subdomain_slug || cfg.custom_domain);
      const missing = !cfg.company_name || !cfg.logo_url || !cfg.primary_color || !hasDomain;
      const skipped = cfg.setup_completed === true || cfg.setup_skipped === true;
      setForm((f) => ({ ...f, ...cfg }));
      if (missing && !skipped) setOpen(true);
    }).catch(() => { /* silent */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const onFile = (kind) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const max = kind === 'favicon_url' ? 300_000 : 1_500_000;
    if (f.size > max) { toast.error(`${kind === 'favicon_url' ? 'Favicon' : 'Logo'} too large (max ${kind === 'favicon_url' ? '300 KB' : '1.5 MB'})`); return; }
    const r = new FileReader();
    r.onload = () => update({ [kind]: r.result });
    r.readAsDataURL(f);
  };

  // Live slug normalisation: lowercase + strip illegals
  const onSlugChange = (v) => update({
    subdomain_slug: v.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 40),
  });

  const stepValid = () => {
    if (step === 0) return form.company_name?.trim().length >= 2 && (form.subdomain_slug?.length >= 3 || form.custom_domain?.length >= 4);
    if (step === 1) return !!form.logo_url; // favicon optional
    if (step === 2) return !!form.primary_color;
    return true;
  };

  const next = () => setStep((s) => Math.min(s + 1, 3));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const saveAndFinish = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/whitelabel/me`, {
        company_name: form.company_name,
        subdomain_slug: form.subdomain_slug || undefined,
        custom_domain: form.custom_domain || undefined,
        logo_url: form.logo_url,
        favicon_url: form.favicon_url || undefined,
        primary_color: form.primary_color,
        booking_url: form.booking_url || undefined,
        setup_completed: true,
      });
      toast.success('Brand setup complete! Your prospects will now see your branding.');
      setOpen(false);
      // Refresh ChromeBrandProvider + banner state
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const skipForNow = async () => {
    // Soft-skip — banner still nags but the modal goes away for this login session.
    try {
      await axios.put(`${API}/api/whitelabel/me`, { setup_skipped: true });
    } catch { /* ignore */ }
    setOpen(false);
  };

  if (loading || !open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <Card className="w-full max-w-xl shadow-2xl" data-testid="brand-setup-wizard">
        <CardContent className="p-6 sm:p-8">
          {/* Header */}
          <div className="flex items-center gap-3 mb-1">
            <Sparkles className="h-5 w-5 text-emerald-600" />
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-700">Step {step + 1} of 4</span>
          </div>
          <h2 className="text-2xl font-heading font-bold mb-1">
            {step === 0 && 'Welcome — let\'s set up your brand'}
            {step === 1 && 'Add your logo'}
            {step === 2 && 'Pick your brand color'}
            {step === 3 && 'Last step — booking link (optional)'}
          </h2>
          <p className="text-sm text-gray-medium mb-6">
            {step === 0 && 'This takes 2 minutes. Your prospects will see this branding everywhere.'}
            {step === 1 && 'A clear logo on a transparent background looks best in proposals + emails.'}
            {step === 2 && 'This colour drives buttons, links, and accent highlights across your branded experience.'}
            {step === 3 && 'Paste your Calendly/Cal.com link to let prospects book a demo directly from your Sales Deck.'}
          </p>

          {/* Step bodies */}
          {step === 0 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="wiz-name">Company name *</Label>
                <Input
                  id="wiz-name"
                  value={form.company_name}
                  onChange={(e) => update({ company_name: e.target.value })}
                  placeholder="e.g. IL Mercato"
                  className="mt-1.5"
                  data-testid="wizard-company-name"
                />
              </div>
              <div>
                <Label htmlFor="wiz-slug">Subdomain *</Label>
                <div className="mt-1.5 flex items-center gap-1">
                  <Input
                    id="wiz-slug"
                    value={form.subdomain_slug}
                    onChange={(e) => onSlugChange(e.target.value)}
                    placeholder="ilmercato"
                    className="flex-1 font-mono text-sm"
                    data-testid="wizard-subdomain"
                  />
                  <span className="text-sm text-gray-medium whitespace-nowrap">.konnect.kaharian.com</span>
                </div>
                <p className="mt-1.5 text-xs text-gray-medium">
                  Lowercase letters, numbers, dashes only. {form.subdomain_slug && `Preview: ${form.subdomain_slug}.konnect.kaharian.com`}
                </p>
              </div>
              <details className="text-xs text-gray-medium">
                <summary className="cursor-pointer hover:text-gray-700">Have your own custom domain? (e.g. konnect.yourcompany.com)</summary>
                <div className="mt-2 pl-4">
                  <Input
                    value={form.custom_domain}
                    onChange={(e) => update({ custom_domain: e.target.value.toLowerCase().trim() })}
                    placeholder="konnect.yourcompany.com"
                    className="font-mono text-xs"
                    data-testid="wizard-custom-domain"
                  />
                  <p className="mt-1 text-[10px]">You'll need to add a CNAME record from your DNS pointing to konnect.kaharian.com after saving.</p>
                </div>
              </details>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              <div>
                <Label>Logo *</Label>
                <div className="mt-2 flex items-center gap-4">
                  <div className="w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center overflow-hidden">
                    {form.logo_url
                      ? <img src={form.logo_url} alt="logo" className="max-w-full max-h-full object-contain" />
                      : <ImageIcon className="h-8 w-8 text-gray-300" />}
                  </div>
                  <div className="flex flex-col gap-2">
                    <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={onFile('logo_url')} data-testid="wizard-logo-input" />
                    <Button variant="outline" onClick={() => logoRef.current?.click()} data-testid="wizard-logo-upload-btn">
                      <Upload className="h-4 w-4 mr-2" /> {form.logo_url ? 'Replace' : 'Upload logo'}
                    </Button>
                    {form.logo_url && (
                      <Button variant="ghost" size="sm" onClick={() => update({ logo_url: '' })}>Remove</Button>
                    )}
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-500">PNG / SVG with transparency · max 1.5 MB.</p>
              </div>

              <div>
                <Label>Favicon <span className="text-gray-400 font-normal">(optional)</span></Label>
                <div className="mt-2 flex items-center gap-4">
                  <div className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center overflow-hidden">
                    {form.favicon_url
                      ? <img src={form.favicon_url} alt="favicon" className="max-w-full max-h-full object-contain" />
                      : form.logo_url
                        ? <img src={form.logo_url} alt="" className="max-w-full max-h-full object-contain opacity-40" />
                        : <ImageIcon className="h-6 w-6 text-gray-300" />}
                  </div>
                  <div className="flex flex-col gap-2">
                    <input ref={faviconRef} type="file" accept="image/png,image/x-icon,image/svg+xml,image/jpeg" className="hidden" onChange={onFile('favicon_url')} data-testid="wizard-favicon-input" />
                    <Button variant="outline" size="sm" onClick={() => faviconRef.current?.click()} data-testid="wizard-favicon-upload-btn">
                      <Upload className="h-4 w-4 mr-2" /> {form.favicon_url ? 'Replace' : 'Upload favicon'}
                    </Button>
                    {form.favicon_url && (
                      <Button variant="ghost" size="sm" onClick={() => update({ favicon_url: '' })}>Remove</Button>
                    )}
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Square PNG / .ico, ideally 64×64 · max 300 KB. {!form.favicon_url && form.logo_url ? 'If empty, we use your logo.' : ''}
                </p>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <Label>Primary color *</Label>
              <div className="grid grid-cols-8 gap-2">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => update({ primary_color: c })}
                    className={`h-12 w-12 rounded-lg border-2 transition-all ${form.primary_color === c ? 'border-gray-900 scale-110 shadow-md' : 'border-transparent hover:scale-105'}`}
                    style={{ backgroundColor: c }}
                    data-testid={`wizard-color-${c}`}
                    aria-label={c}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.primary_color}
                  onChange={(e) => update({ primary_color: e.target.value })}
                  className="w-10 h-10 rounded border cursor-pointer"
                />
                <Input
                  value={form.primary_color}
                  onChange={(e) => update({ primary_color: e.target.value })}
                  className="flex-1 font-mono text-xs"
                  data-testid="wizard-color-hex"
                />
              </div>
              <div className="rounded-lg p-4 text-white text-center font-semibold" style={{ backgroundColor: form.primary_color }}>
                Sample button — this is how your brand colour will look
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="wiz-book">Demo booking URL</Label>
                <Input
                  id="wiz-book"
                  type="url"
                  value={form.booking_url}
                  onChange={(e) => update({ booking_url: e.target.value })}
                  placeholder="https://calendly.com/your-team/discovery"
                  className="mt-1.5"
                  data-testid="wizard-booking-url"
                />
                <p className="mt-1.5 text-xs text-gray-500">
                  Paste your Calendly / Cal.com / Google Calendar booking link. When set, prospects who finish your public Sales Deck see a <strong>"Book directly on my calendar →"</strong> button. Leave blank if you'd rather have prospects fill in a lead-capture form.
                </p>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold mb-2">You're all set 🎉</div>
                <ul className="text-sm text-emerald-900 space-y-1">
                  <li>✓ Branded login at <strong>{form.subdomain_slug ? `${form.subdomain_slug}.konnect.kaharian.com` : form.custom_domain || 'your domain'}</strong></li>
                  <li>✓ Your logo + favicon site-wide</li>
                  <li>✓ <strong>{form.primary_color}</strong> as your accent colour</li>
                  {(user?.role === 'country_operator' || user?.role === 'super_admin')
                    ? <li>✓ Sales Deck shareable links route leads to your inbox</li>
                    : <li>✓ Your customers see your brand on every email, proposal, and portal page</li>}
                </ul>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 mt-8 pt-4 border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={skipForNow}
              data-testid="wizard-skip"
              className="text-gray-500 hover:text-gray-700"
            >
              Skip for now
            </Button>
            <div className="flex gap-2">
              {step > 0 && (
                <Button variant="outline" onClick={back} data-testid="wizard-back">
                  <ChevronLeft className="h-4 w-4 mr-1" /> Back
                </Button>
              )}
              {step < 3 ? (
                <Button
                  onClick={next}
                  disabled={!stepValid()}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="wizard-next"
                >
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={saveAndFinish}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="wizard-finish"
                >
                  {saving ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>) : (<><Check className="h-4 w-4 mr-2" />Finish setup</>)}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BrandSetupWizard;
